import * as THREE from 'three';

/** Figma turn label — Inside-Line node 2423:3448 */
export const MARKER_LAYOUT = {
  height: 80,
  radius: 20,
  padX: 20,
  padY: 24,
  fontLabel: 32,
  colors: {
    labelBg: 0xffffff,
    labelBgOpacity: 0.1,
    text: 0xffffff,
  },
  /** World units per design pixel */
  pxScale: 0.008,
};

/** @type {CanvasRenderingContext2D | null} */
let measureCtx = null;

function getMeasureCtx() {
  if (!measureCtx) {
    const canvas = document.createElement('canvas');
    measureCtx = canvas.getContext('2d');
  }
  return measureCtx;
}

function roundedRectShape(w, h, r) {
  const s = new THREE.Shape();
  s.moveTo(r, 0);
  s.lineTo(w - r, 0);
  s.quadraticCurveTo(w, 0, w, r);
  s.lineTo(w, h - r);
  s.quadraticCurveTo(w, h, w - r, h);
  s.lineTo(r, h);
  s.quadraticCurveTo(0, h, 0, h - r);
  s.lineTo(0, r);
  s.quadraticCurveTo(0, 0, r, 0);
  return s;
}

function shapeToMesh(shape, material, renderOrder = 0) {
  const geo = new THREE.ShapeGeometry(shape);
  const mesh = new THREE.Mesh(geo, material);
  mesh.renderOrder = renderOrder;
  return mesh;
}

function makeFillMaterial(color, opacity) {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: opacity < 1,
    opacity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/**
 * @param {string} text
 * @param {string} font
 * @param {typeof MARKER_LAYOUT} layout
 */
function labelWidthForText(text, font, layout) {
  const ctx = getMeasureCtx();
  const sample = text.trim() || ' ';
  if (!ctx) return Math.ceil(sample.length * layout.fontLabel * 0.6) + layout.padX * 2;
  ctx.font = font;
  const measured = ctx.measureText(sample).width;
  return Math.ceil(measured) + layout.padX * 2;
}

/**
 * @param {number} widthPx
 * @param {typeof MARKER_LAYOUT} layout
 */
function labelRegionForWidth(widthPx, layout) {
  const h = layout.height;
  return {
    x: layout.padX,
    y: layout.padY,
    w: widthPx - layout.padX * 2,
    h: h - layout.padY * 2,
    align: 'center',
  };
}

/**
 * @param {string} text
 * @param {{
 *   font: string;
 *   fontSizePx: number;
 *   color: string;
 *   widthPx: number;
 *   heightPx: number;
 *   align?: CanvasTextAlign;
 *   baseline?: CanvasTextBaseline;
 * }} opts
 */
function createTextTexture(text, opts) {
  const {
    font,
    fontSizePx,
    color,
    widthPx,
    heightPx,
    align = 'center',
    baseline = 'middle',
  } = opts;

  const canvas = document.createElement('canvas');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.max(4, Math.ceil(widthPx * dpr));
  canvas.height = Math.max(4, Math.ceil(heightPx * dpr));

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');

  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, widthPx, heightPx);
  ctx.fillStyle = color;
  ctx.font = font;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  const textX =
    align === 'left' ? 2 : align === 'right' ? widthPx - 2 : widthPx / 2;
  ctx.fillText(text, textX, heightPx / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;

  return { texture, widthPx, heightPx };
}

/**
 * @param {string} text
 * @param {{ x: number; y: number; w: number; h: number; align?: CanvasTextAlign }} region
 */
function createTextPlane(text, region, font, fontSizePx, z) {
  const { texture, widthPx, heightPx } = createTextTexture(text, {
    font,
    fontSizePx,
    color: '#ffffff',
    widthPx: region.w,
    heightPx: region.h,
    align: region.align ?? 'center',
    baseline: 'middle',
  });

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(region.w, region.h), material);
  mesh.position.set(region.x + region.w / 2, region.y + region.h / 2, z);
  mesh.renderOrder = 10;

  return { mesh, texture, canvas: texture.image };
}

/**
 * 3D turn label matching Figma event frame (80px tall, width from measured text).
 */
export function createCornerTurnLabel3d() {
  const L = MARKER_LAYOUT;
  const h = L.height;
  const { colors } = L;
  const labelFont = `400 ${L.fontLabel}px "SF Mono", "Fira Code", ui-monospace, monospace`;

  const group = new THREE.Group();
  group.name = 'corner-turn-label';

  const content = new THREE.Group();
  group.add(content);

  let labelWidth = labelWidthForText('TURN NAME', labelFont, L);
  /** @type {THREE.Mesh} */
  let labelBg;
  /** @type {{ mesh: THREE.Mesh; texture: THREE.Texture; canvas: HTMLCanvasElement }} */
  let labelText;

  const disposeText = (entry) => {
    entry.mesh.geometry.dispose();
    entry.mesh.material.map?.dispose();
    entry.mesh.material.dispose();
  };

  const disposeBg = (mesh) => {
    mesh.geometry.dispose();
    mesh.material.dispose();
  };

  /**
   * @param {string} title
   */
  function rebuildLabel(title) {
    const displayTitle = title.trim() || ' ';
    labelWidth = labelWidthForText(displayTitle, labelFont, L);
    const region = labelRegionForWidth(labelWidth, L);

    if (labelBg) {
      content.remove(labelBg);
      disposeBg(labelBg);
    }
    if (labelText) {
      content.remove(labelText.mesh);
      disposeText(labelText);
    }

    content.position.x = -labelWidth / 2;

    labelBg = shapeToMesh(
      roundedRectShape(labelWidth, h, L.radius),
      makeFillMaterial(colors.labelBg, colors.labelBgOpacity),
      1,
    );
    labelText = createTextPlane(displayTitle, region, labelFont, L.fontLabel, 0.02);

    content.add(labelBg, labelText.mesh);
  }

  rebuildLabel('TURN NAME');

  let scaleMultiplier = 1;
  group.scale.setScalar(L.pxScale * scaleMultiplier);

  return {
    object: group,
    /**
     * @param {number} multiplier
     */
    setScale(multiplier) {
      scaleMultiplier = Math.max(0.1, multiplier);
      group.scale.setScalar(L.pxScale * scaleMultiplier);
    },
    /**
     * @param {string} title
     */
    setContent(title) {
      rebuildLabel(title);
    },
    dispose() {
      if (labelBg) disposeBg(labelBg);
      if (labelText) disposeText(labelText);
    },
  };
}

const _billboardParentQuat = new THREE.Quaternion();

/**
 * Face the camera even when the label is parented under a rotated marker (e.g. start line).
 * @param {THREE.Object3D} labelRoot
 * @param {THREE.Camera} camera
 */
export function billboardCornerLabel(labelRoot, camera) {
  const parent = labelRoot.parent;
  if (!parent) {
    labelRoot.quaternion.copy(camera.quaternion);
    return;
  }
  parent.getWorldQuaternion(_billboardParentQuat);
  labelRoot.quaternion.copy(_billboardParentQuat.invert()).multiply(camera.quaternion);
}
